// frontend/client/src/components/ContactUs.jsx
// Infleuince brand refresh — Syne + Plus Jakarta Sans, violet-to-blue system

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Mail, MessageSquare, Send, Check,
  Twitter, Instagram, Linkedin, ChevronDown, Sparkles,
} from 'lucide-react';

const CONTACT_CSS = `
  .contact-root {
    min-height: 100vh;
    background: var(--bg-base);
    color: var(--text-primary);
    font-family: var(--font-body);
    padding: 48px 16px;
  }
  .contact-input {
    width: 100%;
    padding: 12px 14px;
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    font-family: var(--font-body);
    font-size: 14px;
    color: var(--text-primary);
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
  }
  .contact-input:focus {
    border-color: var(--brand-violet);
    box-shadow: 0 0 0 3px oklch(0.55 0.24 290 / 0.1);
  }
  .contact-input::placeholder { color: var(--text-muted); }
  .contact-label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: var(--text-secondary);
    margin-bottom: 6px;
    font-family: var(--font-body);
  }
  .contact-card {
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: 24px;
    padding: 32px;
    box-shadow: var(--shadow-card);
  }
  .contact-social-link {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-radius: 14px;
    background: var(--bg-surface);
    border: 1px solid var(--border);
    text-decoration: none;
    transition: all 0.2s;
    color: var(--text-primary);
  }
  .contact-social-link:hover {
    border-color: var(--brand-violet);
    background: var(--bg-elevated);
    transform: translateX(3px);
  }

  @media (max-width: 768px) {
    .contact-root { padding: 24px 16px; }
    .contact-main-grid {
      grid-template-columns: 1fr !important;
    }
  }
`;

export default function ContactUs() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '', email: '', category: 'GENERAL', subject: '', message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted]   = useState(false);

  const categoryOptions = [
    { value: 'GENERAL',  label: 'General Inquiry' },
    { value: 'SUPPORT',  label: 'Technical Support / Bug Report' },
    { value: 'BUSINESS', label: 'Business / Partnership' },
    { value: 'PRESS',    label: 'Press / Media' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL ||
        (process.env.NODE_ENV === 'production' ? 'https://www.infleuince.com' : 'http://localhost:5000');
      const res  = await fetch(`${apiUrl}/api/resend/send-contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        setIsSubmitted(true);
        setTimeout(() => { setFormData({ name: '', email: '', category: 'GENERAL', subject: '', message: '' }); setIsSubmitted(false); }, 4000);
      } else {
        alert(`Failed: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  return (
    <div className="contact-root">
      <style>{CONTACT_CSS}</style>
      <div style={{ maxWidth: 960, margin: '0 auto' }}>

        {/* Back */}
        <button onClick={() => navigate('/')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--brand-violet)', marginBottom: 32, fontFamily: 'var(--font-body)' }}>
          <ArrowLeft size={15} /> Back to Home
        </button>

        {/* Header */}
        <div style={{ marginBottom: 40, textAlign: 'center' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 14px', borderRadius: 9999, border: '1px solid var(--border)', background: 'var(--bg-elevated)', fontSize: 11, fontWeight: 700, color: 'var(--brand-violet)', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: 'var(--font-body)', marginBottom: 14 }}>
            <Sparkles size={11} /> Get in Touch
          </span>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(28px,4vw,42px)', fontWeight: 700, color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.02em' }}>
            We'd love to hear from you.
          </h1>
          <p style={{ fontSize: 16, color: 'var(--text-secondary)', margin: '10px 0 0', fontFamily: 'var(--font-body)' }}>
            Send us a message and we'll respond within 24–48 hours.
          </p>
        </div>

        <div className="contact-main-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 24, alignItems: 'start' }}>

          {/* Form */}
          <div className="contact-card">
            {isSubmitted ? (
              <div style={{ textAlign: 'center', padding: '48px 0' }}>
                <div style={{ width: 56, height: 56, borderRadius: 9999, background: 'var(--brand-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: 'var(--shadow-soft)' }}>
                  <Check size={24} color="white" />
                </div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 8px' }}>Message Sent!</h3>
                <p style={{ color: 'var(--text-secondary)', margin: 0, fontFamily: 'var(--font-body)' }}>We'll get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div>
                  <label className="contact-label">Name *</label>
                  <input type="text" name="name" required value={formData.name} onChange={handleChange} className="contact-input" placeholder="Your name" />
                </div>
                <div>
                  <label className="contact-label">Email *</label>
                  <input type="email" name="email" required value={formData.email} onChange={handleChange} className="contact-input" placeholder="you@email.com" />
                </div>
                <div>
                  <label className="contact-label">Inquiry Type *</label>
                  <div style={{ position: 'relative' }}>
                    <select name="category" required value={formData.category} onChange={handleChange} className="contact-input" style={{ appearance: 'none', paddingRight: 40 }}>
                      {categoryOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <ChevronDown size={16} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
                  </div>
                </div>
                <div>
                  <label className="contact-label">Subject *</label>
                  <input type="text" name="subject" required value={formData.subject} onChange={handleChange} className="contact-input" placeholder="e.g., Question about Video Credits" />
                </div>
                <div>
                  <label className="contact-label">Message *</label>
                  <textarea name="message" required rows={5} value={formData.message} onChange={handleChange} className="contact-input" placeholder="Tell us more..." style={{ resize: 'vertical', minHeight: 120 }} />
                </div>
                <button type="submit" disabled={isSubmitting} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px 24px', borderRadius: 12, background: 'var(--brand-gradient)', border: 'none', color: 'white', fontSize: 14, fontWeight: 700, cursor: isSubmitting ? 'wait' : 'pointer', opacity: isSubmitting ? 0.7 : 1, fontFamily: 'var(--font-body)', boxShadow: 'var(--shadow-soft)', transition: 'opacity 0.2s' }}>
                  {isSubmitting ? <><div style={{ width: 16, height: 16, border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />Sending…</> : <><Send size={15} /> Send Message</>}
                </button>
              </form>
            )}
          </div>

          {/* Info sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="contact-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: 'var(--brand-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <MessageSquare size={16} color="white" />
                </div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Direct Contact</h3>
              </div>
              {[
                { label: 'Technical Support', email: 'support@infleuince.com' },
                { label: 'Business Inquiries', email: 'business@infleuince.com' },
                { label: 'Press & Media', email: 'press@infleuince.com' },
              ].map(({ label, email }) => (
                <div key={email} style={{ marginBottom: 14 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', margin: '0 0 2px', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'var(--font-body)' }}>{label}</p>
                  <a href={`mailto:${email}`} style={{ fontSize: 13, color: 'var(--brand-violet)', fontWeight: 600, fontFamily: 'var(--font-body)', textDecoration: 'none' }}>{email}</a>
                </div>
              ))}
            </div>

            <div className="contact-card">
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 16px' }}>Follow Us</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <a href="https://twitter.com/infleuince" target="_blank" rel="noopener noreferrer" className="contact-social-link">
                  <Twitter size={16} style={{ color: '#1DA1F2' }} />
                  <div><p style={{ fontWeight: 700, fontSize: 13, margin: 0, fontFamily: 'var(--font-body)' }}>Twitter</p><p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0, fontFamily: 'var(--font-body)' }}>@infleuince</p></div>
                </a>
                <a href="https://instagram.com/infleuince" target="_blank" rel="noopener noreferrer" className="contact-social-link">
                  <Instagram size={16} style={{ color: '#E1306C' }} />
                  <div><p style={{ fontWeight: 700, fontSize: 13, margin: 0, fontFamily: 'var(--font-body)' }}>Instagram</p><p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0, fontFamily: 'var(--font-body)' }}>@infleuince</p></div>
                </a>
                <a href="https://linkedin.com/company/infleuince" target="_blank" rel="noopener noreferrer" className="contact-social-link">
                  <Linkedin size={16} style={{ color: '#0A66C2' }} />
                  <div><p style={{ fontWeight: 700, fontSize: 13, margin: 0, fontFamily: 'var(--font-body)' }}>LinkedIn</p><p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0, fontFamily: 'var(--font-body)' }}>Infleuince</p></div>
                </a>
              </div>
            </div>

            <div style={{ background: 'var(--brand-gradient-soft)', border: '1px solid oklch(0.88 0.06 285)', borderRadius: 16, padding: '16px 20px' }}>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px' }}>Response Time</p>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6, fontFamily: 'var(--font-body)' }}>
                We typically respond within 24–48 hours on business days. For urgent issues, include "URGENT" in your subject line.
              </p>
            </div>
          </div>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}